<template>
  <div class="user-list-item group" @click="handleCardClick">
    <div class="user-avatar-container">
      <img v-if="userPhoto" :src="userPhoto" :alt="displayName" class="user-avatar">
      <div v-else class="user-avatar user-avatar-placeholder">
        <User class="h-5 w-5 text-rogue-warm-cream" />
      </div>
      <div v-if="isOnline" class="status-indicator status-online" title="Online now">
        <span class="sr-only">Online</span>
      </div>
    </div>
    <div class="user-info">
      <div class="user-name-row">
        <h3 class="user-name">
          {{ displayName }}
        </h3>
        <div class="status-badges">
          <span v-if="isExploringActive" class="badge badge-exploring" title="Currently exploring">
            <Flame class="h-3.5 w-3.5" />
            <span class="sr-only">Exploring</span>
          </span>
        </div>
      </div>
      <p v-if="statusMessage" class="user-status">
        {{ statusMessage }}
      </p>
    </div>
    <div class="user-actions">
      <button
        v-if="!isOwnProfile"
        class="action-button like-button"
        :class="{ 'is-liked': isLiked }"
        :aria-label="isLiked ? 'Unlike' : 'Like'"
        :disabled="isOwnProfile"
        @click.stop="handleLikeClick"
      >
        <Heart class="h-5 w-5" />
      </button>
      <ChevronRight class="action-icon view-indicator" aria-hidden="true" />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { User, Heart, ChevronRight, Flame } from 'lucide-vue-next'
import { useAuthStore } from '../stores/auth'
import { getAvatarImage } from '../lib/imageUtils'

const props = defineProps({
  user: {
    type: Object,
    required: true
  },
  showExplorationMessage: {
    type: Boolean,
    default: false
  },
  showBio: {
    type: Boolean,
    default: false
  },
  isLiked: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['view-profile', 'toggle-like'])

const authStore = useAuthStore()

const isOwnProfile = computed(() => authStore.user?.id === props.user.id)

const displayName = computed(() => props.user.name || 'Anonymous Camper')

const userPhoto = computed(() => {
  const originalUrl =
    props.user.thumbnail_url ||
    props.user.profile_photos?.find(p => p.is_thumbnail)?.photo_url ||
    props.user.profile_photos?.[0]?.photo_url

  return originalUrl ? getAvatarImage(originalUrl) : null
})

const isOnline = computed(() => {
  if (!props.user.last_active) return false
  const fiveMinutesAgo = new Date()
  fiveMinutesAgo.setMinutes(fiveMinutesAgo.getMinutes() - 5)
  return new Date(props.user.last_active) > fiveMinutesAgo
})

const isExploringActive = computed(() => {
  if (!props.user.zin_in_spel || !props.user.zin_in_spel_until) return false
  return new Date(props.user.zin_in_spel_until) > new Date()
})

const statusMessage = computed(() => {
  if (props.showExplorationMessage && props.user.exploration_message) {
    return props.user.exploration_message
  }
  if (props.showBio && props.user.bio) {
    return props.user.bio
  }
  return null
})

function handleCardClick() {
  emit('view-profile', props.user.id)
}

function handleLikeClick() {
  if (!isOwnProfile.value) {
    emit('toggle-like', props.user.id)
  }
}
</script>

<style scoped>
.user-list-item {
  @apply flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100 cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:border-gray-200 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rogue-vibrant-green focus-visible:ring-offset-2;
}
.user-avatar-container {
  @apply relative flex-shrink-0;
}
.user-avatar {
  @apply w-12 h-12 rounded-full object-cover ring-2 ring-white;
}
.user-avatar-placeholder {
  @apply bg-gray-100 flex items-center justify-center;
}
.status-indicator {
  @apply absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full ring-2 ring-white;
}
.status-online {
  @apply bg-green-500;
}
.user-info {
  @apply flex-1 min-w-0;
}
.user-name-row {
  @apply flex items-center gap-2;
}
.user-name {
  @apply font-semibold text-gray-900 truncate text-base leading-tight;
}
.status-badges {
  @apply flex items-center gap-1 flex-shrink-0;
}
.badge {
  @apply inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium;
}
.badge-exploring {
  @apply bg-orange-100 text-orange-700;
}
.user-status {
  @apply text-sm text-gray-600 mt-0.5 line-clamp-1;
}
.user-actions {
  @apply flex items-center gap-2 flex-shrink-0;
}
.action-button {
  @apply p-2 rounded-lg transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rogue-vibrant-green;
}
.like-button {
  @apply text-gray-400 hover:text-red-500 hover:bg-red-50;
}
.like-button.is-liked {
  @apply text-red-500 bg-red-50;
}
.like-button.is-liked .h-5 {
  @apply fill-current;
}
.view-indicator {
  @apply h-5 w-5 text-gray-300;
}
.group:hover .view-indicator {
  @apply text-gray-400;
}
@media (max-width: 640px) {
  .user-list-item {
    @apply p-3;
  }
  .user-avatar {
    @apply w-10 h-10;
  }
  .user-name {
    @apply text-sm;
  }
  .badge {
    @apply text-xs px-1 py-0;
  }
}
.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
